import { BASE_CONFIG } from '@/workflow/_data/config/config';
import type { AuthoredBaseConfig, BaseConfig } from '@/workflow/_contract/config';
import { assertAuthoredWorkflowStructure } from '@/workflow/_pure/config/presentation';

function isConfigRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function mergeConfigTree<T>(defaults: T, override: unknown): T {
    if (override === undefined) return defaults;
    if (!isConfigRecord(defaults) || !isConfigRecord(override)) return override as T;

    for (const discriminator of ['placementKind', 'layoutKind', 'shapeKind', 'mode']) {
        if (
            typeof defaults[discriminator] === 'string'
            && typeof override[discriminator] === 'string'
            && defaults[discriminator] !== override[discriminator]
        ) {
            return override as T;
        }
    }

    const merged: Record<string, unknown> = { ...defaults };
    for (const [key, value] of Object.entries(override)) {
        if (value === undefined) continue;
        merged[key] = mergeConfigTree(defaults[key], value);
    }
    return merged as T;
}

export function resolveBaseConfig(
    config: Partial<AuthoredBaseConfig> | undefined,
): BaseConfig {
    if (config) assertAuthoredWorkflowStructure(config);
    return resolveEffectiveBaseConfig(config as Partial<BaseConfig> | undefined);
}

/** Internal merge for already-effective configs, including runtime visibility suppression. */
export function resolveEffectiveBaseConfig(
    config: Partial<BaseConfig> | undefined
): BaseConfig {
    const defaultSpaceRenderOptions = BASE_CONFIG.spaceRenderOptions ?? {};
    const inputSpaceRenderOptions = config?.spaceRenderOptions ?? {};

    return {
        ...BASE_CONFIG,
        ...(config ?? {}),
        resourceTypeInputSpec: {
            ...BASE_CONFIG.resourceTypeInputSpec,
            ...(config?.resourceTypeInputSpec ?? {}),
            presetResourcesByType: {
                ...(BASE_CONFIG.resourceTypeInputSpec.presetResourcesByType ?? {}),
                ...(config?.resourceTypeInputSpec?.presetResourcesByType ?? {}),
            },
        },
        spaceRenderOptions: {
            ...defaultSpaceRenderOptions,
            ...inputSpaceRenderOptions,
            camera: {
                ...defaultSpaceRenderOptions.camera,
                ...inputSpaceRenderOptions.camera,
                ...(defaultSpaceRenderOptions.camera?.graph === undefined && inputSpaceRenderOptions.camera?.graph === undefined
                    ? {}
                    : {
                        graph: {
                            stations: {
                                ...defaultSpaceRenderOptions.camera?.graph?.stations,
                                ...inputSpaceRenderOptions.camera?.graph?.stations,
                            },
                            views: {
                                ...defaultSpaceRenderOptions.camera?.graph?.views,
                                ...inputSpaceRenderOptions.camera?.graph?.views,
                            },
                            paths: inputSpaceRenderOptions.camera?.graph?.paths
                                ?? defaultSpaceRenderOptions.camera?.graph?.paths ?? [],
                        },
                    }),
            },
            ...(defaultSpaceRenderOptions.activation === undefined && inputSpaceRenderOptions.activation === undefined
                ? {}
                : {
                    activation: {
                        ...(defaultSpaceRenderOptions.activation ?? {}),
                        ...(inputSpaceRenderOptions.activation ?? {}),
                    },
                }),
        },
        geometryScale: {
            ...BASE_CONFIG.geometryScale,
            ...(config?.geometryScale ?? {}),
        },
        domainPresentation: mergeConfigTree(
            BASE_CONFIG.domainPresentation,
            config?.domainPresentation,
        ),
    };
}
